/**
 * Sessions API Service
 * The signed-in devices of the current user (trusted-device sessions).
 *
 * All calls throw on failure, so a failed load is never read as "no devices".
 * Errors are shown by the caller, so the global error toast is skipped.
 */

import { apiClient } from "@/lib/api-client";

const API_BASE = "/api/v1/auth";

export interface DeviceSession {
	id: string;
	deviceLabel: string | null;
	ipLastUsed: string | null;
	createdAt: string;
	lastUsedAt: string;
	expiresAt: string;
	/** True for the session this request was made with. */
	current: boolean;
}

export async function listDeviceSessions(): Promise<DeviceSession[]> {
	const response = await apiClient.get<{
		data: { sessions: DeviceSession[] };
	}>(`${API_BASE}/sessions`, { inlineErrors: true });
	return response.data.data.sessions;
}

export async function revokeDeviceSession(id: string): Promise<void> {
	await apiClient.delete(`${API_BASE}/sessions/${id}`, { inlineErrors: true });
}

/**
 * Signs out every session except the one this request was made with.
 * Returns the number of sessions that were signed out.
 */
export async function revokeOtherDeviceSessions(): Promise<number> {
	const response = await apiClient.post<{ data: { revoked: number } }>(
		`${API_BASE}/sessions/revoke-others`,
		undefined,
		{ inlineErrors: true },
	);
	return response.data.data.revoked;
}

/** HTTP status of a failed request, or undefined when there was no response. */
export function getErrorStatus(error: unknown): number | undefined {
	const err = error as { response?: { status?: number } } | null;
	return err?.response?.status;
}
